import { create } from 'zustand';
import tr from './locales/tr';
import en from './locales/en';
import nl from './locales/nl';
import fr from './locales/fr';

export const translations = { tr, en, nl, fr };

const getBrowserLanguage = () => {
  const lang = navigator.language?.split('-')[0] || 'en';
  return ['tr', 'en', 'nl', 'fr'].includes(lang) ? lang : 'en';
};

// Translation cache management
const CACHE_KEY = 'i18n_translations_cache';
const VERSION_KEY = 'i18n_translations_version';

// Deep merge helper function
const deepMerge = (target, source) => {
  const result = { ...target };
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(result[key] || {}, source[key]);
    } else if (source[key] !== undefined && source[key] !== '') {
      result[key] = source[key];
    }
  }
  return result;
};

// Get cached translations
const getCachedTranslations = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

// Get cached version
const getCachedVersion = () => {
  try {
    return parseInt(localStorage.getItem(VERSION_KEY) || '0', 10);
  } catch {
    return 0;
  }
};

// Save to cache
const saveToCache = (data, version) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(VERSION_KEY, String(version));
  } catch (e) {
    console.warn('Failed to cache translations:', e);
  }
};

// Get merged translations (base + custom overrides)
const getMergedTranslations = (customData) => {
  if (!customData) return translations;
  
  return {
    tr: deepMerge(translations.tr, customData.tr || {}),
    en: deepMerge(translations.en, customData.en || {}),
    nl: deepMerge(translations.nl, customData.nl || {}),
    fr: deepMerge(translations.fr, customData.fr || {})
  };
};

export const useI18nStore = create((set, get) => ({
  language: getBrowserLanguage(),
  translations: translations[getBrowserLanguage()],
  allTranslations: translations,
  version: getCachedVersion(),
  isLoading: false,
  isInitialized: false,
  
  // Initialize translations from cache or API
  initTranslations: async () => {
    const state = get();
    if (state.isInitialized || state.isLoading) return;
    
    set({ isLoading: true });
    
    try {
      // First, check cached data
      const cachedVersion = getCachedVersion();
      const cachedData = getCachedTranslations();
      
      // If we have cache, use it immediately for fast initial load
      if (cachedData && cachedVersion > 0) {
        const merged = getMergedTranslations(cachedData);
        set({
          allTranslations: merged,
          translations: merged[state.language],
          version: cachedVersion
        });
      }
      
      // Then check server version
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const versionRes = await fetch(`${API_URL}/api/translations/version`);
      
      if (versionRes.ok) {
        const versionData = await versionRes.json();
        const serverVersion = versionData.version || 1;
        
        // If server version is newer, fetch new translations
        if (serverVersion > cachedVersion) {
          const transRes = await fetch(`${API_URL}/api/translations`);
          if (transRes.ok) {
            const transData = await transRes.json();
            if (transData.success) {
              const customTranslations = transData.data;
              const merged = getMergedTranslations(customTranslations);
              
              // Save to cache
              saveToCache(customTranslations, serverVersion);
              
              // Update state
              set({
                allTranslations: merged,
                translations: merged[state.language],
                version: serverVersion
              });
            }
          }
        }
      }
    } catch (error) {
      console.warn('Failed to fetch translations from server, using defaults:', error);
    } finally {
      set({ isLoading: false, isInitialized: true });
    }
  },
  
  // Force refresh translations from server
  refreshTranslations: async () => {
    set({ isLoading: true });
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const transRes = await fetch(`${API_URL}/api/translations`);
      
      if (transRes.ok) {
        const transData = await transRes.json();
        if (transData.success) {
          const customTranslations = transData.data;
          const serverVersion = transData.version || 1;
          const merged = getMergedTranslations(customTranslations);
          
          // Save to cache
          saveToCache(customTranslations, serverVersion);
          
          // Update state
          const state = get();
          set({
            allTranslations: merged,
            translations: merged[state.language],
            version: serverVersion
          });
          
          return { success: true, version: serverVersion };
        }
      }
      return { success: false };
    } catch (error) {
      console.error('Failed to refresh translations:', error);
      return { success: false, error };
    } finally {
      set({ isLoading: false });
    }
  },
  
  // Clear cache and reset to defaults
  clearCache: () => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(VERSION_KEY);
    const state = get();
    set({
      allTranslations: translations,
      translations: translations[state.language],
      version: 0,
      isInitialized: false
    });
  },
  
  setLanguage: (lang) => {
    if (['tr', 'en', 'nl', 'fr'].includes(lang)) {
      const state = get();
      set({ 
        language: lang, 
        translations: state.allTranslations[lang] 
      });
    }
  },
  
  t: (key, params) => {
    const keys = key.split('.');
    const state = get();
    
    // First try current translations (merged from DB + base)
    let value = state.translations;
    for (const k of keys) {
      value = value?.[k];
    }
    
    // If not found, fallback to base translations
    if (!value || value === key) {
      const lang = state.language || 'tr';
      value = translations[lang];
      for (const k of keys) {
        value = value?.[k];
      }
    }
    
    let result = value || key;
    
    // Interpolation: replace {{var}} with params
    if (params && typeof result === 'string') {
      Object.entries(params).forEach(([k, v]) => {
        result = result.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v));
      });
    }
    
    return result;
  }
}));

export const useTranslation = () => {
  const { t, language, setLanguage, initTranslations, refreshTranslations, isLoading, version, clearCache } = useI18nStore();
  return { t, language, setLanguage, initTranslations, refreshTranslations, isLoading, version, clearCache };
};

// Büyük harf dönüşümü (CSS uppercase) dile göre doğru yapılsın (tr: i→İ sadece Türkçede).
const syncHtmlLang = (lang) => { if (typeof document !== 'undefined' && lang) document.documentElement.lang = lang; };
syncHtmlLang(useI18nStore.getState().language);
useI18nStore.subscribe((st) => syncHtmlLang(st.language));
